import type { ReactNode } from 'react';
import { cx } from './cx.js';
import { AlertIcon, CheckCircleIcon, InfoIcon } from './icons.js';

/**
 * États : vide, chargement (squelette), message (Alert). Un état vide explique quoi faire,
 * il ne se contente pas de « rien ici ».
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
  compact = false,
  className,
}: {
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  /** Version courte, dans une section (une ligne). */
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div
        className={cx(
          'flex items-center gap-2.5 rounded-2xl border border-dashed border-line-strong px-3.5 py-3 text-ink-3',
          className,
        )}
      >
        {icon ? <span className="shrink-0 text-ink-3">{icon}</span> : null}
        <p className="text-base">{children ?? title}</p>
      </div>
    );
  }
  return (
    <div
      className={cx(
        'flex flex-col items-center rounded-2xl border border-dashed border-line-strong px-5 py-8 text-center',
        className,
      )}
    >
      {icon ? (
        <span className="mb-2.5 grid size-10 place-items-center rounded-full bg-sunken text-ink-2">{icon}</span>
      ) : null}
      {title ? <p className="text-base font-medium text-ink">{title}</p> : null}
      {children ? <div className="mt-1 max-w-sm text-base text-ink-3">{children}</div> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Bloc de squelette : forme de contenu pendant le chargement. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cx('ui-skeleton block rounded-lg', className)} />;
}

/** Squelette d'une liste de cartes (parc, accueil). Annonce « Chargement… » aux lecteurs d'écran. */
export function SkeletonList({ rows = 3, label = 'Chargement…' }: { rows?: number; label?: string }) {
  return (
    <div role="status" className="space-y-3">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rounded-2xl border border-line bg-surface p-(--card-p)">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-28" />
              <Skeleton className="h-4 w-44" />
            </div>
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
          <Skeleton className="mt-4 h-5 w-36 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const ALERT: Record<AlertTone, { box: string; icon: ReactNode }> = {
  neutral: { box: 'border-line bg-sunken text-ink-2', icon: <InfoIcon size={18} /> },
  info: { box: 'border-info-line bg-info-bg text-info-fg', icon: <InfoIcon size={18} /> },
  success: { box: 'border-success-line bg-success-bg text-success-fg', icon: <CheckCircleIcon size={18} /> },
  warning: { box: 'border-warning-line bg-warning-bg text-warning-fg', icon: <AlertIcon size={18} /> },
  danger: { box: 'border-danger-line bg-danger-bg text-danger-fg', icon: <AlertIcon size={18} /> },
};

/** Message dans la page. `role` : `alert` pour une erreur, `status` sinon. */
export function Alert({
  tone = 'info',
  title,
  children,
  action,
  role,
  icon = true,
  className,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  role?: 'alert' | 'status';
  icon?: boolean;
  className?: string;
}) {
  const t = ALERT[tone];
  return (
    <div
      role={role ?? (tone === 'danger' ? 'alert' : 'status')}
      className={cx('flex gap-2.5 rounded-xl border px-3 py-2.5 text-base', t.box, className)}
    >
      {icon ? <span className="mt-0.5 shrink-0">{t.icon}</span> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={title ? 'mt-0.5' : undefined}>{children}</div> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </div>
  );
}
